import React, {
    ComponentProps,
    useCallback,
    useEffect,
    useState,
    ReactElement
} from 'react';
import HBTooltip from '../HBTooltip/HBTooltip';
import { Z_INDEX } from '../../styles/variables';
import {
    CounterContainer,
    ControlButtonContainer,
    InputValue,
    Decrease,
    Increase,
    IncreaseDrawer,
    DecreaseDrawer,
    Container
} from './HBCounter.styles';
import { buttonThemes } from '../HBButton/HBButton.styles';
import { ICustomThemeInput } from '../HBTextField/HBTextField.types';
import { MustIncludeOneOfTestIdProps } from '../common/internal/test-id.types';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';

export interface Props extends Omit<ComponentProps<'input'>, 'size'> {
    /** Current numeric value; clamped to [minValue, maxValue] on render if bounds are set. */
    value: number;
    /** Minimum allowed value; decrease button disables and shows tooltip when reached. */
    minValue?: number;
    /** Maximum allowed value; increase button disables and shows tooltip when reached. */
    maxValue?: number;
    /** Blocks all interaction and shows `disabledTooltip` on hover. @default false */
    disabled?: boolean;
    /** When true, renders increment/decrement buttons alongside the input. */
    showButtons?: boolean;
    /** Tooltip text shown when the entire counter is disabled. */
    disabledTooltip?: string;
    /** Tooltip shown on the decrease button when minValue is reached. Defaults to "You must select at least {minValue}". */
    exceededMinTooltip?: string;
    /** Tooltip shown on the increase button when maxValue is reached. Defaults to "You can only select up to {maxValue}". */
    exceededMaxTooltip?: string;
    /** Called with the new numeric value when the counter changes via buttons or input blur. */
    onChangeValue: (value: number) => void;
    /** Visual theme for the increment and decrement buttons. Accepts any key from `buttonThemes` (e.g. 'primary', 'secondary-solid-primary'). */
    buttonsTheme?: keyof typeof buttonThemes;
    /** Inline styles applied to the tooltip's target container wrapping the counter. */
    counterTargetContainerStyles?: React.CSSProperties;
    /** Custom color for the numeric input value text. */
    valueColor?: string;
    /** Accent color applied to the increment/decrement button hover and counter border. */
    highlightColor?: string;
    /** Custom theme overrides for the inner text field input. */
    inputCustomTheme?: ICustomThemeInput;
    /** Enables drawer-optimized layout with alternate increment/decrement icons. */
    isInDrawer?: boolean;
    /** HTML `name` attribute forwarded to the underlying `<input>` element; also used as the test ID identifier when `testId` and `testIdName` are omitted. */
    name?: string;
}

const HBCounter = ({
    id,
    name,
    testIdName,
    testId,
    value,
    minValue,
    maxValue,
    disabled = false,
    showButtons = true,
    disabledTooltip = 'This value is immutable',
    exceededMinTooltip,
    exceededMaxTooltip,
    onChangeValue,
    counterTargetContainerStyles,
    valueColor,
    highlightColor,
    inputCustomTheme,
    isInDrawer
}: Props & MustIncludeOneOfTestIdProps) => {
    const hasMinValue = minValue != null;
    const hasMaxValue = maxValue != null;
    const [customValue, setCustomValue] = useState(() => {
        if (hasMinValue && value < minValue) {
            return minValue;
        }
        if (hasMaxValue && value > maxValue) {
            return maxValue;
        }
        return value;
    });

    useEffect(() => {
        setCustomValue(customValue);
    }, [customValue]);

    useEffect(() => {
        setCustomValue(value);
    }, [value]);

    const handleOnChange = useCallback(event => {
        const IS_FLOAT_REGEX = /^-?\d*(\.\d?\d?)?$/; // matches the following: 'd', 'd.','d.d', 'd.dd'
        if (new RegExp(IS_FLOAT_REGEX).test(event.target.value)) {
            setCustomValue(parseFloat(event.target.value));
        }
    }, []);

    const handleOnBlur = useCallback(
        event => {
            let newValue = parseFloat(event.target.value);
            if (hasMinValue && newValue < minValue) {
                newValue = minValue;
            } else if (hasMaxValue && newValue > maxValue) {
                newValue = maxValue;
            }

            setCustomValue(newValue);
            onChangeValue(newValue);
        },
        [hasMaxValue, hasMinValue, maxValue, minValue, onChangeValue]
    );

    const decreaseValue = useCallback(() => {
        onChangeValue(customValue - 1);
        setCustomValue(customValue - 1);
    }, [customValue, onChangeValue]);

    const increaseValue = useCallback(() => {
        onChangeValue(customValue + 1);
        setCustomValue(customValue + 1);
    }, [customValue, onChangeValue]);

    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName || id || name,
        suffix: 'counter',
        componentName: 'HBCounter'
    });

    const renderButton = (
        limitValue: number,
        mode: 'decrease' | 'increase',
        onClick: () => void,
        icon: ReactElement,
        tooltipText: string,
        counterTestId: string
    ) => {
        if (!showButtons) {
            return null;
        }

        const target = (
            <ControlButtonContainer
                mode={mode}
                highlightColor={highlightColor}
                disabled={customValue === limitValue || disabled}
                onClick={disabled ? () => {} : onClick}
                isInDrawer={isInDrawer}
                data-testid={`${counterTestId}-${mode}`}
            >
                {icon}
            </ControlButtonContainer>
        );

        if (customValue !== limitValue) {
            return target;
        }

        return (
            <HBTooltip
                placement="top"
                customStyle={{
                    maxWidth: '212px',
                    zIndex: Z_INDEX.TOP_MOST
                }}
                target={target}
                targetContainerStyle={{
                    height: '100%',
                    margin: 0
                }}
            >
                {tooltipText}
            </HBTooltip>
        );
    };

    return (
        <HBTooltip
            forceClose={!disabled}
            customStyle={{ maxWidth: '190px', zIndex: Z_INDEX.TOP_MOST }}
            placement="top"
            targetContainerStyle={{
                ...counterTargetContainerStyles,
                height: '100%'
            }}
            target={
                <Container>
                    <CounterContainer
                        highlightColor={highlightColor}
                        disabled={disabled}
                        isInDrawer={isInDrawer}
                    >
                        {renderButton(
                            minValue,
                            'decrease',
                            decreaseValue,
                            isInDrawer ? <DecreaseDrawer /> : <Decrease />,
                            exceededMinTooltip ||
                                `You must select at least ${minValue}`,
                            finalTestId
                        )}

                        <InputValue
                            id={id}
                            name={name}
                            type="number"
                            pattern="[0-9]"
                            size="xs"
                            value={customValue}
                            disabled={disabled}
                            customTheme={inputCustomTheme}
                            onChange={disabled ? () => {} : handleOnChange}
                            onBlur={disabled ? () => {} : handleOnBlur}
                            min={minValue}
                            max={maxValue}
                            $valueColor={valueColor}
                            $isInDrawer={isInDrawer}
                            testId={finalTestId}
                        />

                        {renderButton(
                            maxValue,
                            'increase',
                            increaseValue,
                            isInDrawer ? <IncreaseDrawer /> : <Increase />,
                            exceededMaxTooltip ||
                                `You can only select up to ${maxValue}`,
                            finalTestId
                        )}
                    </CounterContainer>
                </Container>
            }
        >
            {disabledTooltip}
        </HBTooltip>
    );
};

export default HBCounter;
